import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FileText,
  Shield,
  LogOut,
  CheckCircle,
  User,
  MapPin,
  Phone,
  Upload,
  X,
  ArrowRight,
  ArrowLeft,
  Crown,
  Mail
} from 'lucide-react'
import { Modal, Button, Input } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import { doc, updateDoc, setDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import type { ShippingAddress } from '../../types/user'
import { updatePublicProfile } from '../../services/nicknameService'

// Current versions of terms and privacy policy
export const CURRENT_TERMS_VERSION = '1.0'
export const CURRENT_PRIVACY_VERSION = '1.0'

interface OnboardingModalProps {
  isOpen: boolean
  onComplete: () => void
  onExit: () => void
}

// Step 1: Terms acceptance schema
const termsSchema = z.object({
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'Debes aceptar las condiciones legales para continuar'
  })
})

// Step 2: Profile completion schema with standardized address fields - used for type inference only
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const profileSchema = z.object({
  firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: z.string().min(2, 'Los apellidos deben tener al menos 2 caracteres'),
  addressLine1: z.string().min(5, 'La calle debe tener al menos 5 caracteres'),
  number: z.string().min(1, 'El número es obligatorio'),
  addressLine2: z.string().min(1, 'La información adicional es obligatoria'),
  city: z.string().min(2, 'La ciudad debe tener al menos 2 caracteres'),
  state: z.string().min(2, 'La provincia debe tener al menos 2 caracteres'),
  postalCode: z.string().regex(/^\d{5}$/, 'El código postal debe tener 5 dígitos'),
  country: z.string().default('España'),
  phone: z.string().min(1, 'El número de teléfono es obligatorio'),
  email: z.string().email('Email inválido').optional(), // Required conditionally based on user.email
})

type TermsFormData = z.infer<typeof termsSchema>
type ProfileFormData = z.infer<typeof profileSchema>

const OnboardingModal = ({ isOpen, onComplete, onExit }: OnboardingModalProps) => {
  const [currentStep, setCurrentStep] = useState<'terms' | 'profile'>('terms')

  const [isLoading, setIsLoading] = useState(false)
  const [, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const { user, setUser, signOut } = useAuthStore()

  // Dynamic schema - require email if user doesn't have one
  const getDynamicProfileSchema = () => {
    const baseSchema = {
      firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
      lastName: z.string().min(2, 'Los apellidos deben tener al menos 2 caracteres'),
      addressLine1: z.string().min(5, 'La calle debe tener al menos 5 caracteres'),
      number: z.string().min(1, 'El número es obligatorio'),
      addressLine2: z.string().min(1, 'La información adicional es obligatoria'),
      city: z.string().min(2, 'La ciudad debe tener al menos 2 caracteres'),
      state: z.string().min(2, 'La provincia debe tener al menos 2 caracteres'),
      postalCode: z.string().regex(/^\d{5}$/, 'El código postal debe tener 5 dígitos'),
      country: z.string().default('España'),
      phone: z.string().min(1, 'El número de teléfono es obligatorio'),
    }

    // Add email requirement if user doesn't have email
    if (!user?.email) {
      return z.object({
        ...baseSchema,
        email: z.string().min(1, 'El email es obligatorio para recibir notificaciones del premio').email('Email inválido')
      })
    }

    return z.object(baseSchema)
  }

  // Terms form
  const termsForm = useForm<TermsFormData>({
    resolver: zodResolver(termsSchema),
    defaultValues: {
      acceptTerms: false
    }
  })

  // Profile form
  const profileForm = useForm({
    resolver: zodResolver(getDynamicProfileSchema()),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      addressLine1: '',
      number: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'España',
      phone: user?.phoneNumber || '',
      ...((!user?.email) && { email: '' }) // Add email field if user doesn't have one
    }
  })

  const watchedTerms = termsForm.watch()
  const canProceedToProfile = watchedTerms.acceptTerms

  // Update profile form when user data changes
  useEffect(() => {
    if (user) {
      profileForm.setValue('firstName', user.firstName || '')
      profileForm.setValue('lastName', user.lastName || '')
      profileForm.setValue('phone', user.phoneNumber || '')
    }
  }, [user, profileForm])

  // Reset when modal opens (only when isOpen changes, not when user changes)
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('terms')
      termsForm.reset()
      profileForm.reset({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        addressLine1: '',
        number: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'España',
        phone: user?.phoneNumber || ''
      })
      setPhotoFile(null)
      setPhotoPreview(null)
    }
  }, [isOpen])

  // Handle photo upload
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen válido')
        return
      }

      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('La imagen es demasiado grande. Máximo 2MB.')
        return
      }

      setPhotoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64Result = e.target?.result as string
        setPhotoPreview(base64Result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removePhoto = () => {
    setPhotoFile(null)
    setPhotoPreview(null)
  }

  // Handle terms acceptance
  const handleTermsSubmit = async (data: TermsFormData) => {
    if (!user) {
      console.error('No user found')
      return
    }

    if (!data.acceptTerms) {
      console.error('Terms not accepted')
      return
    }

    console.log('Starting terms acceptance for user:', user.id)
    setIsLoading(true)

    try {
      // Save acceptance to user profile
      const acceptanceData = {
        termsAcceptedAt: new Date(),
        termsVersion: CURRENT_TERMS_VERSION,
        hasAcceptedTerms: true
      }

      const userDocRef = doc(db, 'users', user.id)

      // Use setDoc with merge option - more reliable than getDoc + updateDoc
      console.log('Saving terms acceptance with setDoc merge...')
      await setDoc(userDocRef, {
        ...user,
        ...acceptanceData
      }, { merge: true })

      console.log('Successfully saved terms acceptance')

      // Update local state
      const updatedUser = {
        ...user,
        ...acceptanceData
      }
      setUser(updatedUser)

      // Move to next step
      console.log('Moving to profile step')
      setCurrentStep('profile')

    } catch (error) {
      console.error('Error saving terms acceptance:', error)
      alert('Error al guardar la aceptación de términos. Por favor, intenta de nuevo.')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle profile completion
  const handleProfileSubmit = async (data: ProfileFormData) => {
    if (!user) return

    console.log('Starting profile completion with data:', data)
    console.log('Current user:', user)

    setIsLoading(true)
    try {
      // Create shipping address with standardized fields
      const shippingAddress: ShippingAddress = {
        firstName: data.firstName,
        lastName: data.lastName,
        addressLine1: data.addressLine1,
        number: data.number,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        postalCode: data.postalCode,
        country: data.country,
        phone: data.phone,
        isDefault: true,
        // Include email in shipping address if user doesn't have email in profile
        ...((!user.email && (data as any).email) && { email: (data as any).email })
      }

      // Update user in Firestore
      const userDoc = doc(db, 'users', user.id)
      const updateData: {
        firstName: string
        lastName: string
        displayName: string
        phoneNumber: string
        defaultShippingAddress: ShippingAddress
        shippingAddresses: ShippingAddress[]
        email?: string
        profileCompletedAt: Date
        profileComplete: boolean
        hasCompletedOnboarding: boolean
        photoURL?: string
        canPurchaseTickets: boolean
        contactMethodVerified: string
        emailCollectedAt?: Date
        phoneCollectedAt?: Date
      } = {
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: `${data.firstName} ${data.lastName}`,
        phoneNumber: data.phone || user.phoneNumber,
        defaultShippingAddress: shippingAddress,
        shippingAddresses: [shippingAddress],
        profileComplete: true,
        profileCompletedAt: new Date(),
        hasCompletedOnboarding: true,
        canPurchaseTickets: true,
        contactMethodVerified: (user.email || (data as any).email) && (user.phoneNumber || data.phone) ? 'both' :
                              (user.email || (data as any).email) ? 'email' : 'phone',
        // Save email in user profile if provided and user doesn't have one
        ...(!user.email && (data as any).email && {
          email: (data as any).email,
          emailCollectedAt: new Date()
        }),
        // Save phone in user profile if provided and user doesn't have one
        ...(!user.phoneNumber && data.phone && {
          phoneNumber: data.phone,
          phoneCollectedAt: new Date()
        })
      }

      // Save photo as base64 if provided
      if (photoPreview) {
        updateData.photoURL = photoPreview
      }

      console.log('Saving profile update data:', updateData)
      await updateDoc(userDoc, updateData)
      console.log('Profile saved successfully')

      // Create public profile
      console.log('Creating public profile...')
      await updatePublicProfile(user.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        photoURL: photoPreview || user.photoURL || null,
        showLoyaltyFrame: true
      })
      console.log('Public profile created successfully')

      // Update local user state
      const updatedUser = {
        ...user,
        ...updateData,
        shippingAddresses: [shippingAddress],
        defaultShippingAddress: shippingAddress,
        // Ensure email is also in the user object if it was provided
        ...(!user.email && (data as any).email && {
          email: (data as any).email
        })
      }
      setUser(updatedUser)

      onComplete()
    } catch (error) {
      console.error('Error updating profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExit = async () => {
    try {
      await signOut()
      onExit()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const goToProfile = async () => {
    if (!canProceedToProfile) {
      // Trigger validation to show errors
      await termsForm.trigger()
      return
    }

    if (isLoading) {
      return
    }

    // Manually get form data and validate
    const formData = termsForm.getValues()
    if (formData.acceptTerms) {
      await handleTermsSubmit(formData)
    }
  }

  const goBackToTerms = () => {
    setCurrentStep('terms')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // No close function - must complete or exit
      size="lg"
      showCloseButton={false}
      closeOnBackdropClick={false}
      closeOnEscape={false}
    >
      <div className="relative">
        {/* Premium Header with Progress */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-t-2xl px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Configuración inicial</h2>
                <p className="text-slate-300 text-xs sm:text-sm">Solo dos pasos para empezar</p>
              </div>
            </div>

            {/* Exit button */}
            <button
              onClick={handleExit}
              disabled={isLoading}
              className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              aria-label="Salir y cerrar sesión"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>

          {/* Premium Progress Indicator */}
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 ${currentStep === 'terms' ? 'text-white' : 'text-slate-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep === 'terms' ? 'bg-blue-500' : 'bg-slate-600'
              }`}>
                1
              </div>
              <span className="text-sm font-medium">Términos</span>
            </div>

            <div className={`flex-1 h-0.5 ${currentStep === 'profile' ? 'bg-blue-500' : 'bg-slate-600'}`} />

            <div className={`flex items-center gap-2 ${currentStep === 'profile' ? 'text-white' : 'text-slate-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep === 'profile' ? 'bg-blue-500' : 'bg-slate-600'
              }`}>
                2
              </div>
              <span className="text-sm font-medium">Perfil</span>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-gray-50 min-h-[400px] sm:min-h-[500px] overflow-y-auto max-h-[60vh] sm:max-h-[70vh]">
          <AnimatePresence mode="wait">
            {/* Step 1: Terms & Conditions */}
            {currentStep === 'terms' && (
              <motion.div
                key="terms"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-4 sm:p-6 space-y-4 sm:space-y-6"
              >
                {/* Welcome Header */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Bienvenido a WildDraw
                  </h3>
                  <p className="text-gray-600">
                    Para participar en los sorteos de WildDraw, debes aceptar nuestras condiciones legales.
                  </p>
                </div>

                <form onSubmit={termsForm.handleSubmit(handleTermsSubmit)} className="space-y-6">
                  {/* Terms Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                    <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Puntos importantes del sorteo
                    </h4>

                    <div className="space-y-3 text-sm text-blue-800">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Los ganadores se determinan por el sorteo oficial de la ONCE</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Envío gratuito de premios a toda España</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Solo para mayores de 18 años y residentes en España</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Tus datos están protegidos según la normativa vigente</span>
                      </div>
                    </div>
                  </div>

                  {/* Single Checkbox */}
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 border">
                      <div className="flex items-start gap-3">
                        <input
                          {...termsForm.register('acceptTerms')}
                          id="accept-terms"
                          type="checkbox"
                          className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="accept-terms" className="text-sm text-gray-700 leading-6">
                          He leído y acepto las{' '}
                          <a
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleExit}
                            className="text-blue-600 hover:text-blue-700 underline font-medium"
                          >
                            Condiciones Legales del Sorteo
                          </a>
                          {' '}organizadas por Wocarp. Confirmo que soy mayor de 18 años y residente en España.
                        </label>
                      </div>
                      {termsForm.formState.errors.acceptTerms && (
                        <p className="text-sm text-red-600 mt-2 ml-8">
                          {termsForm.formState.errors.acceptTerms.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 text-center bg-gray-100 p-3 rounded-lg">
                    Después de aceptar, deberás completar tu perfil con los datos de envío.
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 2: Profile Completion */}
            {currentStep === 'profile' && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="p-4 sm:p-6 space-y-4 sm:space-y-6"
              >
                {/* Profile Header */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Completa tu perfil
                  </h3>
                  <p className="text-gray-600 mt-2">
                    Para poder participar en sorteos y recibir tus premios, es <strong>obligatorio</strong> completar tu perfil.
                  </p>
                </div>

                <form onSubmit={profileForm.handleSubmit(handleProfileSubmit as any)} className="space-y-6">
                  {/* Profile Photo */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 border">
                    <h4 className="font-semibold text-gray-900 mb-4">Foto de perfil (opcional)</h4>
                    <div className="text-center">
                      {photoPreview || user?.photoURL ? (
                        <div className="relative inline-block">
                          <img
                            src={photoPreview || user?.photoURL}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                          />
                          {photoPreview && (
                            <button
                              type="button"
                              onClick={removePhoto}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label
                        htmlFor="photo-upload"
                        className="inline-block mt-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors"
                      >
                        {photoPreview ? 'Cambiar foto' : 'Subir foto'}
                      </label>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 border">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Información personal
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Nombre"
                        placeholder="Tu nombre"
                        {...profileForm.register('firstName')}
                        error={profileForm.formState.errors.firstName?.message}
                        leftIcon={<User className="w-5 h-5" />}
                        variant="premium"
                      />
                      <Input
                        label="Apellidos"
                        placeholder="Tus apellidos"
                        {...profileForm.register('lastName')}
                        error={profileForm.formState.errors.lastName?.message}
                        leftIcon={<User className="w-5 h-5" />}
                        variant="premium"
                      />
                    </div>

                    <div className="mt-4">
                      <Input
                        label="Teléfono"
                        placeholder="+34 123 456 789"
                        type="tel"
                        {...profileForm.register('phone')}
                        error={profileForm.formState.errors.phone?.message}
                        leftIcon={<Phone className="w-5 h-5" />}
                        disabled={!!user?.phoneNumber}
                        helperText={user?.phoneNumber ? 'Número verificado durante el inicio de sesión' : undefined}
                        variant="premium"
                      />
                    </div>

                    {/* Email field - only show if user doesn't have email */}
                    {!user?.email && (
                      <div className="mt-4">
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-blue-800 text-sm flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span>Necesitamos tu email para enviarte notificaciones cuando ganes un premio</span>
                          </p>
                        </div>
                        <Input
                          label="Email"
                          placeholder="tu@email.com"
                          type="email"
                          {...profileForm.register('email' as any)}
                          error={(profileForm.formState.errors as any).email?.message}
                          leftIcon={<Mail className="w-5 h-5" />}
                          helperText="Solo para notificaciones de premios"
                          variant="premium"
                        />
                      </div>
                    )}
                  </div>

                  {/* Standardized Shipping Address */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 border">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Dirección de envío
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">
                      Los premios se envían directamente a tu domicilio
                    </p>

                    <div className="space-y-4">
                      <Input
                        label="Calle / Avenida"
                        placeholder="Calle de la Paz, Avenida de la Libertad..."
                        {...profileForm.register('addressLine1')}
                        error={profileForm.formState.errors.addressLine1?.message}
                        variant="premium"
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-1">
                          <Input
                            label="Número"
                            placeholder="123"
                            {...profileForm.register('number')}
                            error={profileForm.formState.errors.number?.message}
                            variant="premium"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Input
                            label="Información adicional"
                            placeholder="Piso 3, Puerta A, Portal 2..."
                            {...profileForm.register('addressLine2')}
                            error={profileForm.formState.errors.addressLine2?.message}
                            variant="premium"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Input
                          label="Ciudad"
                          placeholder="Tu ciudad"
                          {...profileForm.register('city')}
                          error={profileForm.formState.errors.city?.message}
                          variant="premium"
                        />
                        <Input
                          label="Provincia"
                          placeholder="Tu provincia"
                          {...profileForm.register('state')}
                          error={profileForm.formState.errors.state?.message}
                          variant="premium"
                        />
                        <Input
                          label="Código postal"
                          placeholder="12345"
                          {...profileForm.register('postalCode')}
                          error={profileForm.formState.errors.postalCode?.message}
                          variant="premium"
                        />
                      </div>

                      <Input
                        label="País"
                        defaultValue="España"
                        disabled
                        {...profileForm.register('country')}
                        variant="premium"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 text-center">
                    Esta información es necesaria para participar en sorteos. Puedes modificarla después desde tu perfil.
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Premium Footer */}
        <div className="bg-white border-t px-4 sm:px-6 py-3 sm:py-4 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            {/* Back Button */}
            {currentStep === 'profile' && (
              <button
                onClick={goBackToTerms}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium order-3 sm:order-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Atrás
              </button>
            )}

            {/* Spacer for desktop */}
            <div className="hidden sm:block flex-1" />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 order-1 sm:order-2">
              <button
                onClick={handleExit}
                disabled={isLoading}
                className="px-4 py-2 text-center text-gray-600 hover:text-gray-800 transition-colors font-medium order-2 sm:order-1"
              >
                Cerrar sesión
              </button>

              {currentStep === 'terms' ? (
                <Button
                  onClick={goToProfile}
                  disabled={!canProceedToProfile || isLoading}
                  isLoading={isLoading}
                  variant="premium"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  className="order-1 sm:order-2"
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  onClick={profileForm.handleSubmit(handleProfileSubmit as any)}
                  disabled={isLoading}
                  isLoading={isLoading}
                  variant="premium"
                  rightIcon={<Shield className="w-4 h-4" />}
                  className="order-1 sm:order-2"
                >
                  Completar perfil
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default OnboardingModal